import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Upload, FileText, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ProductDescriptionManager = () => {
  const [currentFile, setCurrentFile] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchCurrentFile();
  }, []);

  const fetchCurrentFile = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings`);
      if (response.data.product_description_url) {
        setCurrentFile(response.data.product_description_url);
        setFileType(response.data.product_description_type || 'image');
      }
    } catch (error) {
      console.error('Error fetching product description:', error);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type - only images
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      setMessage({
        type: 'error',
        text: 'Invalid file type. Please upload a JPG or PNG file.'
      });
      return;
    }

    setUploading(true);
    setMessage({ type: '', text: '' });

    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${API_URL}/api/admin/settings/product-description`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setCurrentFile(response.data.url);
      setFileType(response.data.file_type);
      setMessage({
        type: 'success',
        text: 'Product description uploaded successfully!'
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Failed to upload file'
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Product Description</h1>
          <p className="text-gray-600">
            Upload a PDF or image to display on the /product-description page
          </p>
        </div>

        {/* Upload Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Upload File</h2>
          
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-orange-500 transition-colors">
            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-2">
              Click to upload or drag and drop
            </p>
            <p className="text-sm text-gray-500 mb-4">
              JPG or PNG (Max 10MB)
            </p>
            
            <input
              type="file"
              accept=".jpg,.jpeg,.png"
              onChange={handleFileUpload}
              disabled={uploading}
              className="hidden"
              id="file-upload"
            />
            
            <label 
              htmlFor="file-upload"
              className={`inline-block px-6 py-3 bg-orange-500 text-white font-bold rounded-lg cursor-pointer transition-all duration-300 hover:bg-orange-600 ${
                uploading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {uploading ? 'Uploading...' : 'Select File'}
            </label>
          </div>

          {/* Messages */}
          {message.text && (
            <div className={`mt-4 p-4 rounded-lg flex items-center gap-2 ${
              message.type === 'success' 
                ? 'bg-green-50 text-green-800 border border-green-200' 
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              {message.type === 'success' ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <AlertCircle className="w-5 h-5" />
              )}
              <span>{message.text}</span>
            </div>
          )}
        </div>

        {/* Current File Section */}
        {currentFile && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Current File</h2>
            
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg mb-4">
              <FileText className="w-8 h-8 text-orange-500" />
              <div className="flex-1">
                <p className="font-medium text-gray-900">
                  Product Description Image
                </p>
                <p className="text-sm text-gray-500 truncate">{currentFile}</p>
              </div>
            </div>

            {/* Preview */}
            <div className="border rounded-lg overflow-hidden">
              <img
                src={currentFile}
                alt="Product Description"
                className="w-full h-auto"
              />
            </div>

            {/* Public URL Info */}
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800 mb-2">
                <strong>Public URL:</strong> /product-description
              </p>
              <a
                href="/product-description"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline text-sm"
              >
                View Public Page →
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
